import { useCallback, Fragment, FC } from 'react';
import { Button, Icon, Toaster } from '@components';
import { ImageUploadProps } from './interface';
import { useController } from './useController';

const allowedTypes = [
  "image/jpeg",
  "image/png",
  "image/jpg",
];

export const ImageUpload: FC<ImageUploadProps> = ({
    images = [],
    onChange,
    maxImages,
    error,
    className,
    baseImageUrl = '',
}) => {
    
    const {
        setToastMessage,
        setCurrentImageIndex,
        handleImageChange,
        removeImage,
        goToNextImage,
        goToPreviousImage,
        triggerFileInput,
        triggerAddNewImage,
        imageFiles,
        addNewImageRef,
        currentImageIndex,
        toastMessage,
        fileInputRefs,
        imagesWithPreviews,
    } = useController({ baseImageUrl, onChange, images });

    const validFiles = imageFiles.filter((file) => allowedTypes.includes(file.type));

    if (validFiles.length !== imageFiles.length) {
      alert("Seuls les fichiers image de type png, jpg et jpeg sont autorisés.");
    }

    const renderImagePreviewContainer = useCallback(() => {

        return (
            <div className="carousel rounded-box w-full">
                {
                    imagesWithPreviews.length ? (
                        <>
                            {imagesWithPreviews.map((image, index) => (
                                <div
                                    key={index}
                                    className={`carousel-item w-full ${index === currentImageIndex ? 'block' : 'hidden'}`}
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="relative rounded-lg overflow-hidden">
                                            <img
                                                src={image.preview}
                                                alt={`Product Preview ${index + 1}`}
                                                className="w-full object-contain aspect-[4/3]"
                                                // onError={(e) => {
                                                //     console.error(`Error loading image ${index}:`, e);
                                                //     e.currentTarget.src = 'https://via.placeholder.com/400x300?text=Image+non+disponible';
                                                // }}
                                            />
                                            {
                                                imagesWithPreviews.length > 1 && (
                                                    <Fragment>
                                                        <button
                                                            type="button"
                                                            onClick={goToPreviousImage}
                                                            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-8 h-8 flex items-center justify-center rounded-full shadow-md"
                                                        >
                                                            ‹
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={goToNextImage}
                                                            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white w-8 h-8 flex items-center justify-center rounded-full shadow-md"
                                                        >
                                                            ›
                                                        </button>
                                                    </Fragment>
                                                )
                                            }

                                            {
                                                imagesWithPreviews.length > 1 && (
                                                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
                                                        {imagesWithPreviews.map((_, idx) => (
                                                            <button
                                                                key={idx}
                                                                type="button"
                                                                onClick={() => setCurrentImageIndex(idx)}
                                                                className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex
                                                                    ? 'bg-white'
                                                                    : 'bg-white/50 hover:bg-white/75'
                                                                    }`}
                                                            />
                                                        ))}
                                                    </div>
                                                )
                                            }
                                        </div>
                                        <ul className="menu menu-horizontal bg-base-200 rounded-box justify-around">
                                            <li>
                                                <a className="tooltip w-13 h-15 p-2 md:p-1" data-tip="Remplacer">
                                                    <Button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            triggerFileInput(index);
                                                        }}
                                                        className="btn btn-sm btn-outline btn-primary"
                                                    >
                                                        <Icon iconName="Replace" size={20} />
                                                    </Button>
                                                    <input
                                                        ref={el => fileInputRefs.current[index] = el}
                                                        type="file"
                                                        className="hidden"
                                                        accept={allowedTypes.join(",")}
                                                        onChange={(e) => handleImageChange(e, index)}
                                                    />
                                                </a>
                                            </li>
                                            {
                                                imagesWithPreviews.length < (maxImages || Infinity) && (
                                                    <li>
                                                        <a className="tooltip w-13 h-15 p-2 md:p-1" data-tip="Ajouter">
                                                            <Button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    triggerAddNewImage();
                                                                }}
                                                                className="btn btn-sm btn-outline btn-success"
                                                            >
                                                                <Icon iconName="ImagePlus" size={20} />
                                                            </Button>
                                                        </a>
                                                    </li>
                                                )
                                            }
                                            <li>
                                                <a className="tooltip w-13 h-15 p-2 md:p-1" data-tip="Supprimer">
                                                    <Button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            removeImage(index);
                                                        }}
                                                        className="btn btn-sm btn-outline btn-error"
                                                    >
                                                        <Icon iconName="Trash2" size={20} />
                                                    </Button>
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            ))}
                        </>
                    ) : (
                        <label className="flex items-center justify-center w-full min-h-[200px] border-2 border-dashed rounded-lg cursor-pointer">
                            <span className="text-gray-500">Ajouter une image</span>
                            <input
                                type="file"
                                className="hidden"
                                accept={allowedTypes.join(",")}
                                onChange={(e) => handleImageChange(e, null)}
                            />
                        </label>
                    )
                }

                <input
                    ref={addNewImageRef}
                    type="file"
                    className="hidden"
                    accept={allowedTypes.join(",")}
                    onChange={(e) => {
                        console.log("Add new image input changed");
                        handleImageChange(e, null);
                    }}
                />
            </div>
        );
    }, [
        imagesWithPreviews, addNewImageRef, currentImageIndex, 
        goToPreviousImage, goToNextImage, maxImages, 
        setCurrentImageIndex, triggerFileInput, fileInputRefs, 
        handleImageChange, triggerAddNewImage, removeImage]);

    const renderToastMessage = useCallback(() => {
        if (!toastMessage) return null;

        return (
            <Toaster
                message={toastMessage}
                onClose={() => setToastMessage(null)}
                duration={3000}
            />
        );
    }, [setToastMessage, toastMessage]);

    return (
        <div className={`w-full ${className}`}>
            {renderToastMessage()}
            {renderImagePreviewContainer()}

            {
                error && (
                    <div className="mt-2 text-red-500 text-sm flex items-center gap-2">
                        <Icon iconName="TriangleAlert" size={16} />
                        {error}
                    </div>
                )
            }

            {/* Button pour debugger l'état */}
            {/* <button 
                type="button" 
                className="mt-4 px-3 py-1 bg-gray-200 text-xs rounded"
                onClick={() => {
                    logState();
                    console.log("Current component state:", {
                        imagesWithPreviews,
                        currentImageIndex,
                        images
                    });
                }}
            >
                Debug
            </button> */}
        </div>
    );
};
